import { useEffect, useRef, useState } from 'react';
import { Button, Spinner } from 'reactstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import supplierService from '../../services/supplierService';
import './FournisseurCompact.css';

const fournisseurName = (supplier) => supplier.typeFournisseur === 'ENTREPRISE'
    ? supplier.entreprise || supplier.nom || ''
    : [supplier.nom, supplier.prenom].filter(Boolean).join(' ');

const FournisseurForm = ({ onSaved, onCancel, embedded = false }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { id } = useParams();
    const supplierId = embedded ? null : id || location.state?.fournisseur?.id;
    const [original, setOriginal] = useState(null);
    const [name, setName] = useState('');
    const [identityKind, setIdentityKind] = useState('CIN');
    const [identities, setIdentities] = useState({ CIN: '', ICE: '' });
    const identity = identities[identityKind];
    const [phone, setPhone] = useState('');
    const [loading, setLoading] = useState(Boolean(supplierId));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [loadFailed, setLoadFailed] = useState(false);
    const [loadAttempt, setLoadAttempt] = useState(0);
    const submitting = useRef(false);

    useEffect(() => {
        let cancelled = false;
        setError('');
        setLoadFailed(false);
        if (!supplierId) {
            setOriginal(null);
            setName('');
            setIdentityKind('CIN');
            setIdentities({ CIN: '', ICE: '' });
            setPhone('');
            setLoading(false);
            return;
        }
        setLoading(true);
        // Fetch the complete record so hidden supplier data survives an edit.
        supplierService.getSupplierById(supplierId).then(({ data }) => {
            if (cancelled) return;
            const supplier = data.body;
            if (!supplier) throw new Error('Fournisseur introuvable');
            setOriginal(supplier);
            setName(fournisseurName(supplier));
            const kind = supplier.typeFournisseur === 'ENTREPRISE' ? 'ICE' : 'CIN';
            setIdentityKind(kind);
            setIdentities({ CIN: supplier.cin || '', ICE: supplier.ice || '' });
            setPhone(supplier.telephone1 || '');
        }).catch(() => {
            if (cancelled) return;
            setLoadFailed(true);
            setError('Impossible de charger le fournisseur. Réessayez.');
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [supplierId, loadAttempt]);

    const goBack = () => {
        if (onCancel) { onCancel(); return; }
        if (location.pathname.startsWith('/purchases-module')) navigate('/purchases-module/fournisseurs');
        else navigate(-1);
    };

    const changeIdentityKind = (kind) => {
        setIdentityKind(kind);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (submitting.current || loading || loadFailed) return;
        const trimmedName = name.trim();
        if (!trimmedName) {
            setError('Saisissez le nom du fournisseur.');
            return;
        }
        const company = identityKind === 'ICE';
        const samePersonalName = original?.typeFournisseur === 'PARTICULIER'
            && !company && trimmedName === fournisseurName(original);
        const payload = {
            ...(original || {}),
            typeFournisseur: company ? 'ENTREPRISE' : 'PARTICULIER',
            nom: company ? original?.nom || '' : samePersonalName ? original.nom : trimmedName,
            prenom: company ? original?.prenom || '' : samePersonalName ? original.prenom : '',
            entreprise: company ? trimmedName : original?.entreprise || '',
            ice: identities.ICE.trim(),
            cin: identities.CIN.trim(),
            telephone1: phone.trim(),
        };
        submitting.current = true;
        setSaving(true);
        setError('');
        try {
            const response = supplierId
                ? await supplierService.update({ id: supplierId, data: payload })
                : await supplierService.save(payload);
            if (onSaved) onSaved(response.data.body);
            else goBack();
        } catch {
            setError('Enregistrement impossible. Vérifiez les informations et réessayez.');
        } finally {
            submitting.current = false;
            setSaving(false);
        }
    };

    return (
        <section className="supplier-compact supplier-compact-form">
            {!embedded && <header className="supplier-compact-header">
                <h4>{supplierId ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}</h4>
                <Button color="secondary" outline size="sm" onClick={goBack} disabled={saving}>Retour</Button>
            </header>}
            <div className="supplier-compact-panel">
                {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}
                {loading ? <div className="supplier-compact-loading"><Spinner size="sm" /> Chargement…</div>
                    : loadFailed ? <Button size="sm" color="primary" onClick={() => setLoadAttempt(value => value + 1)}>Réessayer</Button>
                    : <form onSubmit={handleSubmit}>
                        <fieldset disabled={saving}>
                            <div className="supplier-compact-fields">
                                <div>
                                    <label htmlFor="supplier-name">Nom <span aria-hidden="true">*</span></label>
                                    <input id="supplier-name" className="form-control" value={name} onChange={event => setName(event.target.value)} required autoFocus autoComplete="name" placeholder="Nom du fournisseur ou de l’entreprise" />
                                </div>
                                <div>
                                    <label htmlFor="supplier-identity">ICE / CIN</label>
                                    <div className="input-group">
                                        <select className="form-select supplier-identity-kind" aria-label="Type de numéro : CIN ou ICE" value={identityKind} onChange={event => changeIdentityKind(event.target.value)}>
                                            <option value="CIN">CIN</option>
                                            <option value="ICE">ICE</option>
                                        </select>
                                        <input id="supplier-identity" className="form-control" value={identity} onChange={event => setIdentities(values => ({ ...values, [identityKind]: event.target.value }))} maxLength={identityKind === 'ICE' ? 15 : 20} placeholder={identityKind === 'ICE' ? 'Numéro ICE' : 'Numéro CIN'} />
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="supplier-phone">Téléphone</label>
                                    <input id="supplier-phone" className="form-control" type="tel" autoComplete="tel" value={phone} onChange={event => setPhone(event.target.value)} placeholder="06…" />
                                </div>
                            </div>
                            <footer className="supplier-compact-form-footer">
                                <small className="text-muted">Seul le nom est obligatoire.</small>
                                {embedded && <Button type="button" size="sm" color="secondary" outline onClick={goBack}>Annuler</Button>}
                                <Button color="primary" size="sm" type="submit">{saving && <Spinner size="sm" className="me-2" />}{saving ? 'Enregistrement…' : 'Enregistrer'}</Button>
                            </footer>
                        </fieldset>
                    </form>}
            </div>
        </section>
    );
};

export default FournisseurForm;
